// frontend/src/components/FinanceReport/Accounting/our_allocations/PaymentAllocationDetailPage.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authFetch } from "../../../api/api"; 
import {
  getAllOurAllocationsById,
  getAllOrders,
  getAllCurrencies,
  postOurAllocation,
  patchOurAllocation,
  deleteOurAllocation,
} from "./requests";
import {
  getAllAccounts,
  getAllProfitAllocations,
  postProfitAllocation,
  patchProfitAllocation,
  deleteProfitAllocation,
} from "./profit_allocations/requests";
import {
  getAllSupplierPrepayments,
  getAllSuppliers,
  postSupplierPrepayment,
  patchSupplierPrepayment,
  deleteSupplierPrepayment,
} from "./supplier_prepayments/requests";

import DistributionTable from "./DistributionTable";
import DocumentsModal from "./DocumentsModal";
import ProfitDocumentsModal from "./profit_allocations/ProfitDocumentsModal";
import SupplierPrepaymentDocumentsModal from "./supplier_prepayments/SupplierPrepaymentDocumentsModal";

const BASE_URL = (process.env.REACT_APP_DJANGO_BACKEND || "").replace(/\/+$/, "");
const ACCOUNTING_URL = `${BASE_URL}/api/accounting/accounting-records/`;

const money = (v, c) => {
  const n = Number(v || 0).toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const s = c?.symbol || "";
  return s ? `${s} ${n}` : n;
};

const EMPTY_ADD = () => ({
  order: "",
  account: "",
  supplier: "",
  amount: "",
  currency: "",
  date: "",
  comment: "",
  payment_agent: "",
  payment_status: "PENDING",
});

async function fetchParentAllocation(recordId, allocationId) {
  const r = await authFetch(`${ACCOUNTING_URL}${recordId}/`);
  if (!r.ok) throw new Error(`HTTP ${r.status}: ${r.statusText}`);
  const data = await r.json();
  return (
    (data.allocations || []).find((a) => String(a.id) === String(allocationId)) ||
    null
  );
}

export default function PaymentAllocationDetailPage() {
  const { id, allocationId } = useParams();
  const navigate = useNavigate();
  const parentAllocationId = Number(allocationId);

  const [ourItems, setOurItems] = useState([]);
  const [profitItems, setProfitItems] = useState([]);
  const [supplierItems, setSupplierItems] = useState([]);
  const [parentAllocation, setParentAllocation] = useState(null);
  const [orders, setOrders] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [companyAccounts, setCompanyAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // inline edit
  const [editing, setEditing] = useState(null); // { kind, id }
  const [editDraft, setEditDraft] = useState({});
  const [editError, setEditError] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // inline add
  const [addType, setAddType] = useState("our");
  const [addDraft, setAddDraft] = useState(EMPTY_ADD());
  const [addError, setAddError] = useState("");
  const [addSaving, setAddSaving] = useState(false);

  // docs modals — храним только id, сам item выводим из актуального списка
  const [ourDocsId, setOurDocsId] = useState(null);
  const [profitDocsId, setProfitDocsId] = useState(null);
  const [supplierDocsId, setSupplierDocsId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [
        ourList,
        profitList,
        supplierList,
        parent,
        orderList,
        currencyList,
        accountsList,
        supplierList2,
      ] = await Promise.all([
        getAllOurAllocationsById(parentAllocationId).catch(() => []),
        getAllProfitAllocations(parentAllocationId).catch(() => []),
        getAllSupplierPrepayments(parentAllocationId).catch(() => []),
        fetchParentAllocation(id, parentAllocationId).catch(() => null),
        getAllOrders().catch(() => []),
        getAllCurrencies().catch(() => []),
        getAllAccounts().catch(() => []),
        getAllSuppliers().catch(() => []),
      ]);
      setOurItems(ourList);
      setProfitItems(profitList);
      setSupplierItems(supplierList);
      setParentAllocation(parent);
      setOrders(orderList);
      setCurrencies(currencyList);
      setCompanyAccounts(accountsList);
      setSuppliers(supplierList2);
    } catch (err) {
      setError(err.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [id, parentAllocationId]);

  useEffect(() => {
    load();
  }, [load]);

  const currenciesById = useMemo(() => {
    const m = {};
    currencies.forEach((c) => {
      m[c.id] = c;
    });
    return m;
  }, [currencies]);

  const parentCurrency = parentAllocation?.currency
    ? currenciesById[parentAllocation.currency] || null
    : null;

  const clientOrders = useMemo(() => {
    if (!parentAllocation) return orders;
    const parentOrder = orders.find(
      (o) => String(o.id) === String(parentAllocation.order)
    );
    const clientId =
      parentOrder?.client?.id ||
      parentOrder?.client_id ||
      parentOrder?.client;
    if (!clientId) return orders;
    return orders.filter((o) => {
      const oc = o.client?.id || o.client_id || o.client;
      return String(oc) === String(clientId);
    });
  }, [orders, parentAllocation]);

  const rows = useMemo(() => {
    const combined = [
      ...ourItems.map((i) => ({ kind: "our", item: i })),
      ...profitItems.map((i) => ({ kind: "profit", item: i })),
      ...supplierItems.map((i) => ({ kind: "supplier", item: i })),
    ];
    combined.sort((a, b) =>
      (a.item.date || "9999").localeCompare(b.item.date || "9999")
    );
    return combined;
  }, [ourItems, profitItems, supplierItems]);

  const totalAmount = Number(parentAllocation?.amount || 0);
  const ourSum = ourItems.reduce((s, i) => s + Number(i.amount || 0), 0);
  const profitSum = profitItems.reduce((s, i) => s + Number(i.amount || 0), 0);
  const supplierSum = supplierItems.reduce((s, i) => s + Number(i.amount || 0), 0);
  const distributed = ourSum + profitSum + supplierSum;
  const remaining = totalAmount - distributed;

  // ── актуальные item'ы для модалок документов ─────────
  const ourDocsItem = useMemo(
    () => ourItems.find((i) => String(i.id) === String(ourDocsId)) || null,
    [ourItems, ourDocsId]
  );
  const profitDocsItem = useMemo(
    () => profitItems.find((i) => String(i.id) === String(profitDocsId)) || null,
    [profitItems, profitDocsId]
  );
  const supplierDocsItem = useMemo(
    () =>
      supplierItems.find((i) => String(i.id) === String(supplierDocsId)) || null,
    [supplierItems, supplierDocsId]
  );

  // ── inline edit ────────────────────────────────────────
  const startEdit = (kind, item) => {
    setEditing({ kind, id: item.id });
    setEditDraft({
      order: kind === "our" ? String(item.order || "") : "",
      account: kind === "profit" ? String(item.account || "") : "",
      supplier: kind === "supplier" ? String(item.supplier || "") : "",
      amount: String(item.amount || ""),
      currency: item.currency ? String(item.currency) : "",
      date: item.date || "",
      comment: item.comment || "",
      payment_agent: item.payment_agent || "",
      payment_status: item.payment_status || "PENDING",
    });
    setEditError("");
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditDraft({});
    setEditError("");
  };

  const saveEdit = async () => {
    setEditError("");
    if (!editing) return;
    const { kind, id: itemId } = editing;

    const amount = Number(editDraft.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return setEditError("Сумма должна быть больше нуля.");
    }

    const payload = {
      amount: amount.toFixed(2),
      currency: editDraft.currency ? Number(editDraft.currency) : null,
      date: editDraft.date || null,
      comment: editDraft.comment || "",
      payment_agent: editDraft.payment_agent || "",
      payment_status: editDraft.payment_status || "PENDING",
    };

    let api;
    if (kind === "our") {
      if (!editDraft.order) return setEditError("Выберите заказ.");
      payload.order = Number(editDraft.order);
      api = patchOurAllocation;
    } else if (kind === "profit") {
      if (!editDraft.account) return setEditError("Выберите счёт компании.");
      payload.account = Number(editDraft.account);
      api = patchProfitAllocation;
    } else {
      if (!editDraft.supplier) return setEditError("Выберите поставщика.");
      payload.supplier = Number(editDraft.supplier);
      api = patchSupplierPrepayment;
    }

    setEditSaving(true);
    try {
      await api(itemId, payload);
      cancelEdit();
      await load();
    } catch (err) {
      setEditError(err.message || "Не удалось сохранить.");
    } finally {
      setEditSaving(false);
    }
  };

  // ── add ────────────────────────────────────────────────
  const handleAdd = async () => {
    setAddError("");
    const amount = Number(addDraft.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return setAddError("Введи сумму больше нуля.");
    }

    const payload = {
      allocation: parentAllocationId,
      amount: amount.toFixed(2),
      currency: addDraft.currency ? Number(addDraft.currency) : null,
      date: addDraft.date || null,
      comment: addDraft.comment || "",
      payment_agent: addDraft.payment_agent || "",
      payment_status: addDraft.payment_status || "PENDING",
    };

    let api;
    if (addType === "our") {
      if (!addDraft.order) return setAddError("Выберите заказ.");
      payload.order = Number(addDraft.order);
      api = postOurAllocation;
    } else if (addType === "profit") {
      if (!addDraft.account) return setAddError("Выберите счёт компании.");
      payload.account = Number(addDraft.account);
      payload.type = "income";
      api = postProfitAllocation;
    } else {
      if (!addDraft.supplier) return setAddError("Выберите поставщика.");
      payload.supplier = Number(addDraft.supplier);
      payload.type = "income";
      api = postSupplierPrepayment;
    }

    setAddSaving(true);
    try {
      await api(payload);
      setAddDraft(EMPTY_ADD());
      await load();
    } catch (err) {
      setAddError(err.message || "Не удалось добавить.");
    } finally {
      setAddSaving(false);
    }
  };

  // ── delete ─────────────────────────────────────────────
  const handleDelete = async (kind, item) => {
    try {
      if (kind === "our") await deleteOurAllocation(item.id);
      else if (kind === "profit") await deleteProfitAllocation(item.id);
      else await deleteSupplierPrepayment(item.id);
      await load();
    } catch (err) {
      setError(err.message || "Не удалось удалить.");
    }
  };

  const openDocs = (kind, item) => {
    if (kind === "our") setOurDocsId(item.id);
    else if (kind === "profit") setProfitDocsId(item.id);
    else setSupplierDocsId(item.id);
  };

  if (loading) {
    return <div className="p-8 text-gray-500">Загрузка…</div>;
  }

  const remainingPositive = Number(remaining) > 0.01;

  return (
    <div className="mx-auto max-w-7xl p-6">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 text-sm text-blue-600 hover:underline"
      >
        ← Назад
      </button>

      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Клиентское назначение #{parentAllocationId}
          </p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">
            Распределение платежа
          </h1>
          {parentAllocation && (
            <p className="mt-1 text-sm text-slate-500">
              Заказ:{" "}
              {parentAllocation.order_code || `#${parentAllocation.order}`}
            </p>
          )}
        </div>
      </header>

      {error && (
        <div className="mb-4 whitespace-pre-wrap rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* СВОДКА */}
      <section className="mb-5 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">
              Сумма назначения
            </p>
            <p className="text-2xl font-bold text-slate-900">
              {money(totalAmount, parentCurrency)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-slate-500">
              Не распределено
            </p>
            <p
              className={`text-xl font-bold ${
                remainingPositive ? "text-amber-700" : "text-emerald-700"
              }`}
            >
              {money(remaining, parentCurrency)}
            </p>
          </div>
        </div>

        {(() => {
          const pct = (v) =>
            totalAmount > 0
              ? Math.min(100, Math.max(0, (Number(v) / totalAmount) * 100))
              : 0;
          return (
            <>
              <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="bg-blue-500 transition-all"
                  style={{ width: `${pct(ourSum)}%` }}
                  title={`Наша оплата поставщику: ${money(
                    ourSum,
                    parentCurrency
                  )}`}
                />
                <div
                  className="bg-violet-500 transition-all"
                  style={{ width: `${pct(profitSum)}%` }}
                  title={`В прибыль компании: ${money(
                    profitSum,
                    parentCurrency
                  )}`}
                />
                <div
                  className="bg-emerald-500 transition-all"
                  style={{ width: `${pct(supplierSum)}%` }}
                  title={`Предоплата поставщику: ${money(
                    supplierSum,
                    parentCurrency
                  )}`}
                />
              </div>

              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
                <span className="inline-flex items-center gap-1.5 text-blue-700">
                  <span className="h-2 w-2 rounded-full bg-blue-500" />
                  <span>
                    <b>Наша оплата поставщику</b> —{" "}
                    {money(ourSum, parentCurrency)}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-violet-700">
                  <span className="h-2 w-2 rounded-full bg-violet-500" />
                  <span>
                    <b>В прибыль компании</b> —{" "}
                    {money(profitSum, parentCurrency)}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-emerald-700">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  <span>
                    <b>Предоплата поставщику</b> —{" "}
                    {money(supplierSum, parentCurrency)}
                  </span>
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 ${
                    remainingPositive ? "text-amber-700" : "text-emerald-700"
                  }`}
                >
                  <span className="h-2 w-2 rounded-full bg-slate-300" />
                  <span>
                    <b>
                      {remainingPositive
                        ? "Не распределено"
                        : "✓ Распределено полностью"}
                    </b>
                    {remainingPositive &&
                      ` — ${money(remaining, parentCurrency)}`}
                  </span>
                </span>
              </div>
            </>
          );
        })()}
      </section>

      <DistributionTable
        rows={rows}
        currenciesById={currenciesById}
        companyAccounts={companyAccounts}
        suppliers={suppliers}
        orders={clientOrders}
        currencies={currencies}
        editing={editing}
        editDraft={editDraft}
        setEditDraft={setEditDraft}
        editError={editError}
        editSaving={editSaving}
        addType={addType}
        setAddType={(v) => {
          setAddType(v);
          setAddError("");
        }}
        addDraft={addDraft}
        setAddDraft={setAddDraft}
        addSaving={addSaving}
        addError={addError}
        onStartEdit={startEdit}
        onSaveEdit={saveEdit}
        onCancelEdit={cancelEdit}
        onDelete={handleDelete}
        onOpenDocs={openDocs}
        onAdd={handleAdd}
      />

      {/* ДОКУМЕНТЫ — item берётся из актуального списка */}
      <DocumentsModal
        open={Boolean(ourDocsItem)}
        item={ourDocsItem}
        onClose={() => setOurDocsId(null)}
        onChanged={load}
      />
      <ProfitDocumentsModal
        open={Boolean(profitDocsItem)}
        item={profitDocsItem}
        onClose={() => setProfitDocsId(null)}
        onChanged={load}
      />
      <SupplierPrepaymentDocumentsModal
        open={Boolean(supplierDocsItem)}
        item={supplierDocsItem}
        onClose={() => setSupplierDocsId(null)}
        onChanged={load}
      />
    </div>
  );
}